import type { EventCollector } from "../EventCollector";
import { EventForMessages } from "../utils/TagManager";
import type { AnalyticsContext } from "./AnalyticsContext";
import { uuidv7 } from "../utils/uuidv7";

export { WebAnalyticsEventStructure };

interface DateInfo {
  created_at: string;
  created_at_integer: number;
}

interface UrlInfo {
  url: string;
}

type MergedEventData<T extends EventForMessages> = T &
  DateInfo &
  UrlInfo & {
    event_type: string;
    crm_infoproduct_id: number;
    crm_team_id: number;
    protodeal_infoproduct_id: number;
    protodeal_business_unit_id: number;
    custom_data: string | null;
  };

class WebAnalyticsEventStructure {
  private _eventType = "WebAnalyticsEvent";
  private _eventCollector: EventCollector;

  constructor(eventCollector: EventCollector) {
    this._eventCollector = eventCollector;
  }

  get eventType() {
    return this._eventType;
  }

  getCurrentDate(): DateInfo {
    const timestamp = new Date();
    const datetime =
      timestamp.getUTCFullYear() +
      "-" +
      ("00" + (timestamp.getUTCMonth() + 1)).slice(-2) +
      "-" +
      ("00" + timestamp.getUTCDate()).slice(-2) +
      " " +
      ("00" + timestamp.getUTCHours()).slice(-2) +
      ":" +
      ("00" + timestamp.getUTCMinutes()).slice(-2) +
      ":" +
      ("00" + timestamp.getUTCSeconds()).slice(-2);

    return {
      created_at: datetime,
      created_at_integer: timestamp.valueOf(),
    };
  }

  getCurrentUrl(): UrlInfo {
    return {
      url: location.href,
    };
  }

  private flattenContext(
    context: AnalyticsContext,
  ): Record<string, string | number> {
    const base: Record<string, string | number> = {
      context_type: context.type,
      page_id: context.pageId,
      page_content_revision_id: context.pageContentRevisionId,
    };

    if (context.type === "website") {
      base.website_id = context.websiteId;
    } else if (context.type === "funnel") {
      base.funnel_id = context.funnelId;
      base.funnel_step_id = context.funnelStepId;
    }

    return base;
  }

  private buildEventPage(context: AnalyticsContext): string {
    const entityId =
      context.type === "website" ? context.websiteId : context.funnelId;
    return `${entityId}_${context.pageId}_${context.pageContentRevisionId}`;
  }

  /**
   * Funnel decision trace: the funnel-router (injected by the content SSR into funnel pages) keeps
   * a cumulative, ordered trace of every decision the visitor went through in
   * `localStorage["pfr:trace:{funnelId}"]` — hops `{stepId, type, snapshotId, childStepId, label,
   * rebucketed, at}`, deduped by stepId, written on click just before navigating. Split hops are
   * attached as `funnel_experiments` and gate hops as `funnel_routing` (wrapped in `{hops: [...]}`,
   * matching the ClickHouse JSON columns on `web_event`), so ANY webanalytics event of a funnel
   * page — e.g. a thank-you page view — carries the full experiment/routing history.
   */
  private getFunnelDecisionsToMessage(
    funnelId: number,
  ): Record<string, { hops: unknown[] }> {
    try {
      const raw = localStorage.getItem(`pfr:trace:${funnelId}`);
      if (!raw) {
        return {};
      }
      const trace = JSON.parse(raw) as Array<{ type?: string } | null>;
      if (!Array.isArray(trace) || trace.length === 0) {
        return {};
      }

      const experiments = trace.filter(
        (hop) => hop && hop.type === "split_test",
      );
      const routing = trace.filter((hop) => hop && hop.type === "logic_gate");

      const result: Record<string, { hops: unknown[] }> = {};
      if (experiments.length > 0) {
        result.funnel_experiments = { hops: experiments };
      }
      if (routing.length > 0) {
        result.funnel_routing = { hops: routing };
      }
      return result;
    } catch {
      return {};
    }
  }

  /**
   * VSL rotation context (misma familia que funnel_experiments: columna JSON
   * propia en web_event, NO custom_data). Adjunta las asignaciones de VSL
   * servidas en esta carga de página — las mantiene VslServing en memoria —
   * a TODO evento webanalytics: así los deciles y el ended del plugin webinar
   * (serializado en el contenido publicado, intocable sin re-guardar cada
   * landing) quedan atribuidos a variante sin modificarlo. Si el propio
   * evento ya trae `vsl_serving` (el error de serving trae `{error}`), se
   * fusiona en vez de pisarse. Páginas sin VSL: no se añade la clave.
   */
  private getVslServingToMessage(
    dataVslServing: unknown,
  ): Record<string, unknown> {
    try {
      const merged: Record<string, unknown> =
        dataVslServing && typeof dataVslServing === "object"
          ? { ...(dataVslServing as Record<string, unknown>) }
          : {};
      const assignments = this._eventCollector.vsl?.servedAssignments ?? [];
      if (assignments.length > 0) {
        merged.assignments = assignments;
      }
      return Object.keys(merged).length > 0 ? { vsl_serving: merged } : {};
    } catch {
      return {};
    }
  }

  mergeData<T extends EventForMessages>(data: T): MergedEventData<T> {
    const context = this._eventCollector.context;

    const finalDataObject = {
      ...data,
      ...this._eventCollector.user.getAdditionalDataToMessage(),
      ...this._eventCollector.tracking.getAdditionalDataToMessage(),
      // Ad click history: attached to every event of the page load that
      // captured a new click ID (the ad-entry PAGE, deliberately redundant:
      // if one event is dropped, another delivers — the visitor-store is
      // idempotent). Subsequent pages only need the visitor_id to match.
      ...(this._eventCollector.clickIds.capturedThisPageLoad
        ? this._eventCollector.clickIds.getAdditionalDataToMessage()
        : {}),
      ...this._eventCollector.experiment.getAdditionalDataToMessage(),
      ...this.getCurrentDate(),
      ...this.getCurrentUrl(),
      ...{ event_type: this.eventType },
      ...{ crm_infoproduct_id: Number(this._eventCollector.crmInfoproductId) },
      ...{ crm_team_id: Number(this._eventCollector.crmTeamId) },
      ...{ protodeal_infoproduct_id: Number(this._eventCollector.protodealInfoproductId) },
      ...{ protodeal_business_unit_id: Number(this._eventCollector.protodealBusinessUnitId) },
      ...(context ? this.flattenContext(context) : {}),
      ...(context ? { event_page: this.buildEventPage(context) } : {}),
      ...(context && context.type === "funnel"
        ? this.getFunnelDecisionsToMessage(context.funnelId)
        : {}),
      ...this.getVslServingToMessage(
        (data as Record<string, unknown>)["vsl_serving"],
      ),
      custom_data: this.buildCustomData(
        (data as Record<string, unknown>)["custom_data"],
        (data as Record<string, unknown>)["event_id"],
      ),
    };

    for (const property in finalDataObject) {
      if (finalDataObject[property] === "") {
        delete finalDataObject[property];
      }
    }

    // El `event_id` canónico viaja DENTRO de custom_data, nunca como clave de
    // primer nivel: el schema del lambda analytics-events es
    // `additionalProperties: false` y una clave nueva arriba tumbaría el
    // evento entero en validación (y con él la conciliación que venía a
    // arreglar). custom_data ya es columna JSON en ClickHouse: cero migración.
    delete (finalDataObject as unknown as Record<string, unknown>)["event_id"];

    return finalDataObject as MergedEventData<T>;
  }

  generateMessage<T extends EventForMessages>(data: T): MergedEventData<T> {
    return this.mergeData(data);
  }

  // custom_data = lo que puso la página (setCustomData) + los flags de
  // entorno (ad_blocker / local_storage) + la PROCEDENCIA de la atribución
  // (`utm_via`) cuando las UTMs no vinieron como `utm_*` en la URL sino
  // rescatadas del parámetro empaquetado `protoctx` ("pctx") o resueltas por
  // la regla de Whop Ads ("whop") — ver Tracking.ts — + la
  // identidad del evento, de la carga de página y los píxeles disparados. En
  // ClickHouse custom_data es columna JSON, así que añadir claves aquí no
  // requiere migración. Si el custom_data de la página no parsea como JSON,
  // se envía intacto: antes preservar lo suyo que adjuntar lo nuestro.
  //
  // El marcador se resuelve AQUÍ, al construir el mensaje, en vez de mutar el
  // customData del collector en el init: así viaja en TODOS los eventos
  // aunque la página llame a setCustomData después (es un reemplazo completo
  // y nos lo borraría) y sin pisar ninguna de sus claves.
  //
  // `eventCustomData`: claves POR-EVENTO que un emisor adjunta pasando un
  // objeto `custom_data` en el data de do() — lo usa `onScheduledEvent` para
  // los uuids de Calendly y el progreso de VSL. Van al final (ganan a las
  // claves de página) y solo se aceptan objetos planos: el custom_data de
  // página (string) sigue su camino de siempre.
  //
  // Detrás de todo, las claves del propio SDK, que nadie puede pisar:
  // `event_id`, `page_load_id` y `pixels`.
  private buildCustomData(
    eventCustomData?: unknown,
    eventId?: unknown,
  ): string | null {
    const raw = this._eventCollector.customData;
    const perEvent: Record<string, unknown> =
      eventCustomData &&
      typeof eventCustomData === "object" &&
      !Array.isArray(eventCustomData)
        ? (eventCustomData as Record<string, unknown>)
        : {};
    try {
      const base: Record<string, unknown> = raw ? JSON.parse(raw) : {};
      const attributionVia = this._eventCollector.tracking?.attributionVia;
      const pageLoadId = this.getPageLoadId();
      const firedPixels = this.getFiredPixels();
      return JSON.stringify({
        ...base,
        ...this._eventCollector.environmentFlags,
        ...(attributionVia ? { utm_via: attributionVia } : {}),
        ...perEvent,
        // Identidad del evento: la canónica si el emisor la trae (optin,
        // cita, deciles...) o una acuñada al vuelo. Va detrás de todo para
        // que ni el custom_data de la página ni el del evento la pisen.
        event_id:
          typeof eventId === "string" && eventId !== "" ? eventId : uuidv7(),
        // La carga de página, igual en todos sus eventos: junto a la posición
        // en `pixels` identifica cada disparo de píxel (ver pageLoadId en
        // EventCollector). Por el mismo motivo, nadie la pisa.
        ...(pageLoadId !== "" ? { page_load_id: pageLoadId } : {}),
        ...(firedPixels.length > 0 ? { pixels: firedPixels } : {}),
      });
    } catch {
      return raw;
    }
  }

  // Un collector sin la clave (stub de test, versión antigua) no la añade:
  // acuñar una aquí daría un id distinto en cada evento, que no sirve para nada.
  private getPageLoadId(): string {
    try {
      const pageLoadId = this._eventCollector.pageLoadId;
      return typeof pageLoadId === "string" ? pageLoadId : "";
    } catch {
      return "";
    }
  }

  // Píxeles de navegador disparados en esta carga de página hasta el momento
  // ([{n: network, p: pixel_id, e: nombre en la red}]). Es lo que permite
  // cuadrar en ClickHouse la copia navegador con la copia servidor
  // (conversion_dispatch) por event_id. Un collector sin módulo de píxeles
  // (stub de test, versión antigua) simplemente no añade la clave.
  private getFiredPixels(): unknown[] {
    try {
      return this._eventCollector.pixels?.firedPixels ?? [];
    } catch {
      return [];
    }
  }

  getEndpoint(): string {
    return this._eventCollector.config.CFG_ENDPOINTS[
      this.eventType + "Endpoint"
    ];
  }
}
